// src/frontend/js/modern/core/fragment-manager.js
import { eventBus, EVENTS } from './event-bus.js';
import { block, unblock } from '../shared/ui-helpers.js';
import { normalizeItems } from "../shared/store-helpers";
import { syncGateway } from './sync-gateway.js';

const FRAGMENT_GLUE = window.wlfmc_l10n?.fragments_index_glue || '.';

/**
 * Fragment refs handled by point-updates (NEVER sent to server).
 * Counters, mini-lists, badges — these use DisplayStore + syncCounterWithStore.
 */
const EXCLUDED_REFS = new Set([
	'wishlist_counter',
	'waitlist_counter',
	'list_counter',
	'save_for_later_counter',
]);

class FragmentManager {
	constructor() {
		this._timeout = null;
		this._controller = null;
		this._replaceLock = false;
	}

	get isReplacing() {
		return this._replaceLock;
	}

	/**
	 * Collect fragment configs from DOM elements carrying
	 * `.wlfmc-wishlist-fragment` class + `data-fragment-options` attribute.
	 * Counter fragments are ALWAYS excluded (handled by point-updates).
	 */
	retrieveFragments(search = {}) {
		let fragments = null;
		const container = search.container || document;
		if (search.fragments) {
			fragments = search.fragments;
		} else {
			fragments = Array.from(container.querySelectorAll('.wlfmc-wishlist-fragment'));
		}
		// FIX: Explicitly exclude counter wrappers even if they have .wlfmc-wishlist-fragment class.
		// Mini-wishlists are managed by BaseListUI/CounterRegistry, not FragmentManager.
		fragments = fragments.filter(el => {
			// Exclude by explicit data-fragment-ref
			const ref = el.dataset.fragmentRef || '';
			if (EXCLUDED_REFS.has(ref)) return false;
			// Exclude by structural class (Mini-Wishlist / Counter Wrapper)
			// This prevents the mini-wishlist from being treated as a server-rendered table fragment.
			if (el.classList.contains('wlfmc-counter-wrapper')) return false;
			return true;
		});
		if (search.s) {
			fragments = fragments.filter(el =>
				!el.dataset.fragmentRef || el.dataset.fragmentRef === search.s
			);
		}
		if (!fragments || !fragments.length) return null;
		const options = {};
		fragments.forEach(el => {
			const classes = el.className.split(' ').filter(c => c && c !== 'exists');
			const id = classes.join(FRAGMENT_GLUE);
			try {
				options[id] = JSON.parse(el.dataset.fragmentOptions || '{}');
			} catch (e) { /* skip malformed */ }
		});
		return Object.keys(options).length ? options : null;
	}

	/**
	 * Load fresh HTML for fragments from the server.
	 * Uses admin-ajax POST (bypasses page cache).
	 */
	loadFragments(search = {}, success = null, successArgs = []) {
		if (this._replaceLock) return;
		clearTimeout(this._timeout);
		if (this._controller) {
			this._controller.abort();
			this._controller = null;
		}
		this._timeout = setTimeout(() => {
			this._controller = new AbortController();
			search = { firstLoad: true, ...search };
			const fragments = this.retrieveFragments(search);
			if (!fragments) {
				if (success) success.apply(null, successArgs);
				return;
			}
			const formData = new FormData();
			formData.append('action', 'wlfmc_load_fragments');
			formData.append('context', 'frontend');
			const nonce = window.wlfmc_l10n?.ajax_nonce?.load_fragments || '';
			formData.append('nonce', nonce);
			const file = new File([JSON.stringify(fragments)], 'fragment.json');
			formData.append('fragments_file', file);
			const url = window.wlfmc_l10n?.admin_url;
			block('.wlfmc-wishlist-table-wrapper, .wlfmc-save-for-later-table-wrapper');
			fetch(url, {
				method: 'POST',
				body: formData,
				credentials: 'same-origin',
				signal: this._controller.signal,
			})
				.then(response => response.json())
				.then(data => {
					unblock('.wlfmc-wishlist-table-wrapper, .wlfmc-save-for-later-table-wrapper');
					if (data.fragments) {
						this.replaceFragments(data.fragments);
						// FIX: Use the single SyncGateway path so display data is properly
						// extracted and routed to DisplayStore (same as full sync).
						// This ensures display data doesn't stay only in StateCache.
						if (data.products) {
							const products = normalizeItems(data.products);
							syncGateway.applyHydrationData('wishlist', products, products.length);
						}
						if (data.waitlist) {
							const waitlist = normalizeItems(data.waitlist);
							syncGateway.applyHydrationData('waitlist', waitlist, waitlist.length);
						}
					}
					if (success) success.apply(null, successArgs);
					this._controller = null;
				})
				.catch(error => {
					if (error.name !== 'AbortError') {
						console.warn('[FragmentManager] Load failed:', error);
					}
					unblock('.wlfmc-wishlist-table-wrapper, .wlfmc-save-for-later-table-wrapper');
					this._controller = null;
				});
		}, 300);
	}

	/**
	 * Replace matched DOM elements with freshly rendered HTML.
	 */
	replaceFragments(fragments) {
		this._replaceLock = true;
		Object.entries(fragments).forEach(([id, html]) => {
			const selector = '.' + id
				.split(FRAGMENT_GLUE)
				.filter(c => c && c !== 'exists' && c !== 'with-count')
				.join('.');
			const targets = document.querySelectorAll(selector);
			if (!targets.length) return;
			const temp = document.createElement('div');
			temp.innerHTML = html;
			let source = temp.querySelector(selector);
			if (!source) {
				for (const child of temp.children) {
					if (child.matches(selector)) {
						source = child;
						break;
					}
				}
			}
			if (source) {
				targets.forEach(el => {
					const clone = source.cloneNode(true);
					if (el.dataset.fragmentOptions && !clone.dataset.fragmentOptions) {
						clone.dataset.fragmentOptions = el.dataset.fragmentOptions;
					}
					el.replaceWith(clone);
				});
			}
		});
		eventBus.emit(EVENTS.CONTENT_UPDATED);
		const $ = window.jQuery;
		if ($) $(document).trigger('wlfmc_init_after_ajax');
		setTimeout(() => { this._replaceLock = false; }, 300);
	}

	reloadFragments() {
		this.loadFragments();
	}
}

export const fragmentManager = new FragmentManager();
export default fragmentManager;
